import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adApi } from '../../../api/ad.api';
import { StrategyDailyMetric } from '../../../types/Ad.api.type';
import { Card } from '../../ui/card';
import { KpiChart } from './charts/KpiChart';
import { MetricsTable } from './charts/MetricsTable';
import { RewardBreakdownSection } from './RewardBreakdownSection';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

/** KPI 목표선 (Phase 2 전략) */
const KPI_GOALS: Record<string, number> = {
  rewardWatch: 70,
  adWatchPerUser: 1.5,
  activeUsers: 100,
};

const getDefaultRange = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 14);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
};

export const ComprehensiveDashboardTab = () => {
  const defaultRange = getDefaultRange();
  const [from, setFrom] = useState(defaultRange.from);
  const [to, setTo] = useState(defaultRange.to);

  const { data: metricsData, isLoading } = useQuery({
    queryKey: ['strategyMetrics', from, to],
    queryFn: () => adApi.getStrategyMetrics(from, to).then((res) => res.data),
    meta: { hideGlobalLoader: true },
  });

  const { data: deploys } = useQuery({
    queryKey: ['strategyDeploys'],
    queryFn: () => adApi.getDeploys().then((res) => res.data),
    meta: { hideGlobalLoader: true },
  });

  const metrics = metricsData?.data ?? [];

  if (isLoading) {
    return <div className="text-center py-8 text-gray-500">로딩 중...</div>;
  }

  return (
    <div className="space-y-6">
      {/* 헤더 + 기간 선택 */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h3 className="text-base font-bold text-gray-900">종합 대시보드 (Phase 2)</h3>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="h-8 px-2 text-xs border rounded bg-white"
          />
          <span className="text-gray-400">~</span>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="h-8 px-2 text-xs border rounded bg-white"
          />
        </div>
      </div>

      {/* 리워드 수익 비교 (풀 폭) */}
      {metrics.length > 0 && (
        <KpiChart
          title="리워드 수익 비교"
          data={metrics}
          dataKey="tossRewardRevenue"
          dataKey2="estimatedRewardRevenue"
          dataKey3="estimatedNetProfit"
          color="#f59e0b"
          color2="#3b82f6"
          color3="#10b981"
          label1="토스 리워드 수익"
          label2="추정 리워드 수익"
          label3="추정 순손익"
          dashed3
          height={280}
          deploys={deploys}
          source="TOSS"
        />
      )}

      {/* 조각 경제 추이 (풀 폭) */}
      {metrics.length > 0 && (
        <KpiChart
          title="조각 경제 추이"
          data={metrics}
          dataKey="pieceIssuance"
          dataKey2="exchangeAmount"
          dataKey3="tossPointPayment"
          color="#ef4444"
          color2="#8b5cf6"
          color3="#f97316"
          label1="조각 발행"
          label2="환전 토스포인트"
          label3="토스포인트 지급"
          height={240}
          deploys={deploys}
          source="ONLYME"
        />
      )}

      {/* KPI 그래프 그리드 */}
      {metrics.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <KpiChart
            title="일 리워드 시청"
            data={metrics}
            dataKey="rewardWatch"
            color="#3b82f6"
            goal={KPI_GOALS.rewardWatch}
            deploys={deploys}
            source="ONLYME"
          />
          <KpiChart
            title="1인당 평균 광고"
            data={metrics}
            dataKey="adWatchPerUser"
            color="#8b5cf6"
            goal={KPI_GOALS.adWatchPerUser}
            deploys={deploys}
            source="ONLYME"
          />
          <KpiChart
            title="활성 유저"
            data={metrics}
            dataKey="activeUsers"
            color="#10b981"
            goal={KPI_GOALS.activeUsers}
            deploys={deploys}
            source="ONLYME"
          />
          <KpiChart
            title="토스 수익 (원)"
            data={metrics}
            dataKey="tossRevenue"
            color="#f59e0b"
            goal={undefined}
            deploys={deploys}
            source="TOSS"
          />
          <KpiChart
            title="타입별 수익 (원)"
            data={metrics}
            dataKey="tossRewardRevenue"
            dataKey2="tossBannerRevenue"
            dataKey3="tossImageRevenue"
            dataKey4="tossInterstitialRevenue"
            color="#3b82f6"
            color2="#f97316"
            color3="#10b981"
            color4="#f43f5e"
            label1="리워드"
            label2="배너"
            label3="이미지배너"
            label4="전면광고"
            deploys={deploys}
            source="TOSS"
          />
          <KpiChart
            title="토스 eCPM (원)"
            data={metrics}
            dataKey="tossEcpm"
            color="#ef4444"
            deploys={deploys}
            source="TOSS"
          />
          <KpiChart
            title="토스 CTR (%)"
            data={metrics}
            dataKey="tossCtr"
            color="#06b6d4"
            deploys={deploys}
            source="TOSS"
          />
          <KpiChart
            title="투표 수"
            data={metrics}
            dataKey="totalVotes"
            color="#6366f1"
            deploys={deploys}
            source="ONLYME"
          />
          <KpiChart
            title="출석 수"
            data={metrics}
            dataKey="totalAttendance"
            color="#14b8a6"
            deploys={deploys}
            source="ONLYME"
          />
          <KpiChart
            title="신규유저 수"
            data={metrics}
            dataKey="newUsers"
            dataKey2="actualSignups"
            color="#f97316"
            color2="#10b981"
            label1="토스 (수동)"
            label2="실제 가입 (자동)"
            deploys={deploys}
            source="TOSS"
          />
          <KpiChart
            title="D1 리텐션 (%)"
            data={metrics}
            dataKey="retentionD1New"
            dataKey2="retentionD1Total"
            color="#ec4899"
            color2="#a855f7"
            label2="전체 D1"
            deploys={deploys}
            source="TOSS"
          />
          <TouchpointBarChart data={metrics} />
        </div>
      )}

      {/* 리워드 체계 분석 */}
      <RewardBreakdownSection from={from} to={to} />

      {/* 데이터 테이블 */}
      {metrics.length > 0 && <MetricsTable data={metrics} />}
    </div>
  );
};

// ==================== 터치포인트 바 차트 ====================

const TouchpointBarChart = ({ data }: { data: StrategyDailyMetric[] }) => {
  const chartData = data.map((d) => ({
    date: d.date.slice(5),
    ctr: d.bannerImpressions > 0 ? Math.round((d.bannerClicks / d.bannerImpressions) * 10000) / 100 : 0,
  }));

  return (
    <Card>
      <div className="p-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">내부 배너 CTR 추이</h4>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} unit="%" />
            <Tooltip formatter={(value) => `${value}%`} />
            <Bar dataKey="ctr" fill="#60a5fa" name="CTR" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};
